import React from 'react'
import { WebStorageStateStore, InMemoryWebStorage } from 'oidc-client-ts'
import { AuthProvider } from 'react-oidc-context'
import { TokenSync } from './TokenSync'
import { DeviceGate } from '../Components/DeviceGate'

const authUrl = import.meta.env.VITE_AUTH_URL as string
const authScope = (import.meta.env.VITE_AUTH_SCOPE as string) || 'kds step-up-auth'

// Explicit metadata prevents oidc-client-ts from fetching .well-known/openid-configuration in dev.
// `issuer` is required by oidc-client-ts internally but is not used for OIDC validation
// since we're running a pure OAuth2 flow (no openid scope, no id_token).
const devMetadata = import.meta.env.DEV
  ? {
      metadata: {
        issuer: (import.meta.env.VITE_AUTH_ISSUER as string) || authUrl,
        authorization_endpoint: `${authUrl}/connect/authorize`,
        token_endpoint: `${authUrl}/connect/token`,
      },
    }
  : {}

// After the code exchange completes the URL is still /callback?code=...; rewrite
// it to / so BrowserRouter (which mounts only after isAuthenticated) lands on
// the kitchen display instead of an unmatched route.
const onSigninCallback = () => {
  window.history.replaceState({}, document.title, '/')
}

export const authConfig = {
  // `authority` is used by oidc-client-ts for OAuth2 discovery in production;
  // in dev, devMetadata above overrides it with explicit endpoints.
  authority: authUrl,
  client_id: import.meta.env.VITE_AUTH_CLIENT_ID as string,
  redirect_uri: `${window.location.origin}/callback`,
  scope: authScope,
  response_type: 'code',
  // Pure OAuth2 flow — no id_token or userinfo.
  loadUserInfo: false,
  automaticSilentRenew: true,
  accessTokenExpiringNotificationTimeInSeconds: 300,
  silent_redirect_uri: `${window.location.origin}/silent-callback`,
  userStore: new WebStorageStateStore({ store: new InMemoryWebStorage() }),
  stateStore: new WebStorageStateStore({ store: window.sessionStorage }),
  onSigninCallback,
  ...devMetadata,
}

export const AuthWrapper = ({ children }: { children: React.ReactNode }) => (
  <AuthProvider {...authConfig}>
    <DeviceGate>
      <TokenSync>{children}</TokenSync>
    </DeviceGate>
  </AuthProvider>
)
